/**
 * Prompts and response parsing for the AI-assisted lesson steps: a comprehension question on
 * one part of the text, and grading of an open answer. Pure functions; the network call
 * lives in data/gemini.ts. Every parser returns null on anything unexpected, so the player
 * can fall back to its deterministic exercise or to self-check.
 */
import type { ChoiceExercise } from './exercises.ts';
import type { OpenTask } from './lesson-plan.ts';

export interface PartSentence {
  text: string;
  ru: string;
}

const LANGUAGE_NAME: Readonly<Record<string, string>> = { zh: 'китайском', en: 'английском', tr: 'турецком' };

/** Pulls the first JSON object out of a model reply (tolerates ```json fences and prose). */
export function extractJson(raw: string): unknown {
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start === -1 || end <= start) return null;
  try {
    return JSON.parse(raw.slice(start, end + 1)) as unknown;
  } catch {
    return null;
  }
}

export function questionPrompt(lang: string, lessonTitle: string, part: readonly PartSentence[]): string {
  const text = part.map((s) => s.text).join(' ');
  const ru = part.map((s) => s.ru).join(' ');
  return [
    `Ты — преподаватель. Ученик (взрослый, уровень выше среднего) только что прочитал часть текста на ${LANGUAGE_NAME[lang] ?? 'иностранном'} языке из урока «${lessonTitle}».`,
    'Составь ОДИН вопрос на понимание смысла этой части: кто, что сделал, почему, что почувствовал. Не спрашивай про перевод отдельных слов и не задавай очевидных вопросов.',
    'Вопрос и варианты — на русском. Ровно 3 варианта, один верный, неверные — правдоподобные, по содержанию этой же части.',
    'Ответь только JSON без пояснений: {"question": "...", "options": ["...", "...", "..."], "answer": <индекс верного 0-2>}',
    '',
    `Текст: ${text}`,
    `Перевод (для тебя, ученик его не видит): ${ru}`,
  ].join('\n');
}

/** A validated AI question as a choice exercise, or null. */
export function parseQuestion(raw: string, id: string, instruction: string): ChoiceExercise | null {
  const data = extractJson(raw) as { question?: unknown; options?: unknown; answer?: unknown } | null;
  if (!data || typeof data.question !== 'string' || !Array.isArray(data.options)) return null;
  const options = data.options.filter((o): o is string => typeof o === 'string' && o.trim() !== '').map((o) => o.trim());
  const answer = typeof data.answer === 'number' ? data.answer : Number.NaN;
  if (options.length !== 3 || new Set(options).size !== 3 || !Number.isInteger(answer) || !options[answer]) return null;
  const question = data.question.trim();
  if (!question || question.length > 300) return null;
  return {
    kind: 'choice',
    id,
    instruction,
    prompt: question,
    options,
    correct: [options[answer]!],
    multi: false,
  };
}

export interface Grade {
  ok: boolean;
  /** Short feedback in Russian. */
  feedback: string;
  /** The learner's answer, corrected (target language). */
  corrected?: string;
}

export function gradePrompt(lang: string, lessonTitle: string, task: OpenTask, answer: string, context: string): string {
  return [
    `Ты — доброжелательный, но честный преподаватель ${LANGUAGE_NAME[lang] ?? 'иностранного'} языка. Урок: «${lessonTitle}».`,
    `Задание (${task.title}): ${task.prompt}`,
    task.outline ? `План пересказа: ${task.outline.join(' / ')}` : '',
    task.keywords.length > 0 ? `Слова, которые нужно использовать: ${task.keywords.join(', ')}` : '',
    context ? `Текст урока (для справки): ${context}` : '',
    '',
    `Ответ ученика: ${answer}`,
    '',
    'Оцени: ответ по теме, написан на нужном языке, понятен, использует хотя бы часть нужных слов. Мелкие ошибки допустимы.',
    'feedback — 1–3 коротких предложения по-русски: что хорошо и главная ошибка, если есть. corrected — исправленный вариант ответа ученика на изучаемом языке (если исправлять нечего — его же ответ).',
    'Ответь только JSON: {"ok": true|false, "feedback": "...", "corrected": "..."}',
  ]
    .filter((line, i, all) => line !== '' || all[i - 1] !== '')
    .join('\n');
}

export function parseGrade(raw: string): Grade | null {
  const data = extractJson(raw) as { ok?: unknown; feedback?: unknown; corrected?: unknown } | null;
  if (!data || typeof data.ok !== 'boolean' || typeof data.feedback !== 'string' || !data.feedback.trim()) return null;
  const corrected = typeof data.corrected === 'string' && data.corrected.trim() ? data.corrected.trim() : undefined;
  return { ok: data.ok, feedback: data.feedback.trim(), ...(corrected ? { corrected } : {}) };
}

/** Which of the keywords the answer uses (case-insensitive outside Chinese). */
export function usedKeywords(answer: string, keywords: readonly string[]): string[] {
  const lower = answer.toLowerCase();
  return keywords.filter((k) => lower.includes(k.toLowerCase()));
}
